import {
  NavigationBarSection,
  SpotlightCardProps,
  NavigationCategory,
  NavButtonProps,
  NavButton,
  NavigationBarProps,
  NavigationBarLink,
} from '@everlywell/consumer-ui';
import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Icon,
  Image,
  Link as UiKitLink,
} from '@everlywell/ui-kit';
import { Gift, House, ShoppingCart } from '@phosphor-icons/react';
import { Link } from 'gatsby';
import React from 'react';
import { RESULTS_ROOT, STORE_ROOT } from 'utils/constants/urls';
import { isFullUrl } from 'utils/helpers';

import { NavigationTracking } from './NavigationBar.tracking';

/*
 * We can't import all possible icon names from Phosphor Icons, so we define them here.
 * And limit the possible values to the ones we know we have in Contentful.
 * If we add more icons to Contentful, we need to add them here as well.
 */
const iconMap = {
  Gift,
  House,
  ShoppingCart,
} as const;

type IconName = keyof typeof iconMap;

/**
 * Transforms a menu drawer from Contentful into NavigationCategory format
 */
export const transformMenuDrawer = (
  drawer: Queries.Maybe<Queries.ContentfulMenuDrawer001>,
): NavigationCategory => {
  const isDirectLink = drawer?.url;

  if (isDirectLink) {
    return transformMenuLink(drawer);
  }

  return transformCategory(drawer);
};

const transformCategory = (
  drawer: Queries.Maybe<Queries.ContentfulMenuDrawer001>,
): NavigationCategory => {
  // Transform menu spotlights data
  const spotlightCards = transformSpotlightCards(drawer?.menuSpotlights || []);

  const isBottomButtonFullUrl = isFullUrl(drawer?.mainCta?.url || '');

  const bottomButtonProps: ButtonProps = {
    ...(isBottomButtonFullUrl
      ? { href: drawer?.mainCta?.url || '', as: UiKitLink }
      : { to: drawer?.mainCta?.url || '', as: Link }),
  };

  const category: NavigationCategory = {
    label: drawer?.menuLabel || '',
    hasOnlySpotlightCards: drawer?.hasOnlySpotlightCards || false,
    spotlightCards: spotlightCards,
    sections: transformMenuDrawerGroups(drawer?.menuDrawerGroups || []),
    buttonProps: drawer?.buttonProperties
      ? transformButtonProperties(drawer.buttonProperties)
      : undefined,
    bottomButton: drawer?.mainCta?.label ? (
      <Button
        {...bottomButtonProps}
        width={['100%', null, null, 'auto']}
        onClick={() =>
          NavigationTracking.trackMainDrawerButtonClick(
            drawer?.mainCta?.label || '',
          )
        }
      >
        {drawer?.mainCta?.label || ''}
      </Button>
    ) : undefined,
  };

  return category;
};

const transformMenuLink = (
  link: Queries.Maybe<Queries.ContentfulMenuDrawer001>,
): NavigationBarLink => ({
  label: link?.menuLabel || '',
  href: link?.url || '',
  isSearchable: false,
  buttonProps: link?.buttonProperties
    ? transformButtonProperties(link.buttonProperties)
    : undefined,
});

const transformButtonProperties = (
  buttonProperties: Queries.Maybe<Queries.ButtonPropertiesFragment>,
): NavButtonProps =>
  ({
    rightIcon: buttonProperties?.icon ? (
      <Icon as={iconMap[buttonProperties.icon as IconName]} size={24} />
    ) : undefined,
    ...(buttonProperties?.theme?.backgroundColor && {
      backgroundColor: buttonProperties.theme.backgroundColor,
    }),
    color: buttonProperties?.theme?.textColor || 'tints.black',
  } as NavButtonProps);

/**
 * Transforms menu drawer groups from Contentful into NavigationBarSection format
 */
export const transformMenuDrawerGroups = (
  menuDrawerGroups: readonly Queries.Maybe<Queries.ContentfulMenuDrawerGroup001>[],
): NavigationBarSection[] =>
  menuDrawerGroups?.map((group) => ({
    title: group?.groupLabel || '',
    links:
      group?.menuLinks?.map((link) => ({
        label: link?.label || '',
        href: link?.url || '',
        additionalContent: link?.secondaryLabel || undefined,
        isSearchable: link?.isSearchable ?? true,
      })) || [],
  })) || [];

/**
 * Transforms menu spotlights data from Contentful into SpotlightCardProps
 */
export const transformSpotlightCards = (
  menuSpotlights: readonly Queries.Maybe<Queries.ContentfulMenuSpotlight001>[],
): SpotlightCardProps[] =>
  menuSpotlights?.map((spotlight) => ({
    title: spotlight?.title || '',
    description: spotlight?.description?.description || '',
    linkComponent: Link,
    onClick: () =>
      NavigationTracking.trackSpotlightClick(spotlight?.title || ''),
    imageElement: (
      <>
        <Box display={['block', null, null, 'none']} height="100%">
          <Image
            src={spotlight?.image?.mobileImage?.url || ''}
            alt={spotlight?.image?.mobileImageAltText || ''}
            height="100%"
            objectFit="cover"
          />
        </Box>
        <Box display={['none', null, null, 'block']}>
          <Image
            src={spotlight?.image?.desktopImage?.url || ''}
            alt={spotlight?.image?.desktopImageAltText || ''}
            width="100%"
          />
        </Box>
      </>
    ),
    href: spotlight?.cta?.url || '',
    button: (
      <Button
        colorScheme="whiteCS"
        size="sm"
        as={Link}
        to={spotlight?.cta?.url || ''}
      >
        {spotlight?.cta?.label || ''}
      </Button>
    ),
    backgroundColor: spotlight?.theme?.backgroundColor || 'tints.lightCream',
  })) || [];

export const transformMobileDrawerLinks = (
  drawers?: readonly Queries.Maybe<Queries.ContentfulMenuDrawer001>[],
): NavigationBarProps['mobileBottomButtons'] =>
  drawers
    // Filter out drawers with submenus. We only want direct links.
    ?.filter((drawer) => drawer?.url)
    .map((link) => {
      const hasFullUrl = isFullUrl(link?.url || '');

      return (
        <NavButton
          width="100%"
          as={hasFullUrl ? UiKitLink : Link}
          to={link?.url || ''}
          key={link?.menuLabel}
          backgroundColor={link?.buttonProperties?.theme?.backgroundColor}
          color={link?.buttonProperties?.theme?.textColor}
        >
          <Flex gap="2">
            {link?.menuLabel}
            <Icon
              as={iconMap[link?.buttonProperties?.icon as IconName]}
              size={24}
            />
          </Flex>
        </NavButton>
      );
    });

export const getProfileUrl = (isLoggedIn: boolean) =>
  isLoggedIn ? `${RESULTS_ROOT}/dashboard` : `${STORE_ROOT}/login`;
